import { useEffect, useState } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import ReactGA from 'react-ga4';

import Main from '@modules/main/Main';
import Login from '@modules/login/Login';
import Register from '@modules/register/Register';
import ForgetPassword from '@modules/forgot-password/ForgotPassword';
import RecoverPassword from '@modules/recover-password/RecoverPassword';
import TestCategories from '@pages/TestCategories';
import TestTypes from "./pages/TestTypes";
import TestProfile from "./pages/TestProfile";
import Backup from "./pages/BackupDB";
import Departments from '@pages/Departments';
import Dashboard from '@pages/Dashboard';
import User from '@pages/Users';
import Patient from '@pages/Patients';
import TermsConditions from '@pages/TermsConditions';
import IncomeByReference from "@pages/IncomeByReference";
import FinanceReport from "@pages/FinanceReport";
import PatientsReports from '@pages/PatientsReports';
import TestReceipt from '@pages/TestReceipt';
import Addtestentry from '@pages/Addtestentry';
import PatientLog from '@pages/PatientLog';
import PosReceipt from '@pages/Posreceipt';
import BillPdf from '@pages/Billpdf';
import Billing from '@pages/Billing';
import BillingBillPdf from '@pages/BillingBillPdf';
import BillingPosReceipt from '@pages/BillingPosReceipt';
import Payments from '@pages/Payments';
import PatientFilter from '@pages/Patientfilter';
import UserType from '@pages/UserType';
import Profile from '@pages/profile/Profile';
import ChangePassword from '@pages/ChangePassword';
import Settings from '@pages/Settings';
import Expenses from '@pages/Expenses';
import LandingPage from '@pages/LandingPage';
import PublicRoute from './routes/PublicRoute';
import PrivateRoute from './routes/PrivateRoute';
import Doctor from '@pages/Doctors';
import { useWindowSize } from '@app/hooks/useWindowSize';
import { calculateWindowSize } from '@app/utils/helpers';
import { setWindowSize } from '@app/store/reducers/ui';
import { setCurrentUser } from './store/reducers/auth';

import { firebaseAuth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';

import { useAppDispatch, useAppSelector } from './store/store';
import { Loading } from './components/Loading';
import { AuthProvider } from './contexts/AuthContext';
import useTokenExpiration from '@app/hooks/useTokenExpiration';

const { VITE_NODE_ENV } = import.meta.env;

const AppContent = () => {
  useTokenExpiration();

  const windowSize = useWindowSize();
  const screenSize = useAppSelector((state) => state.ui.screenSize);
  const dispatch = useAppDispatch();
  const location = useLocation();

  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const size = calculateWindowSize(windowSize.width);
    if (screenSize !== size) {
      dispatch(setWindowSize(size));
    }
  }, [windowSize, dispatch, screenSize]);

  useEffect(() => {
    if (VITE_NODE_ENV === 'production') {
      ReactGA.send({ hitType: 'pageview', page: location.pathname });
    }
  }, [location]);

  useEffect(() => {
    const handleError = () => setHasError(true);
    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  if (hasError) {
    return (
      <div style={{ padding: 20, textAlign: 'center' }}>
        <h2>Something went wrong</h2>
        <button onClick={() => window.location.reload()}>Reload</button>
      </div>
    );
  }

  return (
    <>
      <Routes>

        {/* Public routes */}
        <Route path="/home"             element={<PublicRoute><LandingPage /></PublicRoute>} />
        <Route path="/login"            element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/register"         element={<PublicRoute><Register /></PublicRoute>} />
        <Route path="/forgot-password"  element={<PublicRoute><ForgetPassword /></PublicRoute>} />
        <Route path="/recover-password" element={<PublicRoute><RecoverPassword /></PublicRoute>} />


        {/* Private routes inside Main layout */}
        <Route path="/" element={<PrivateRoute><Main /></PrivateRoute>}>

          <Route index                          element={<Dashboard />} />
          <Route path="users"                   element={<User />} />
          <Route path="user-type"               element={<UserType />} />
          <Route path="patients"                element={<Patient />} />
          <Route path="patients-reports"        element={<PatientsReports />} />
          <Route path="backup"                  element={<Backup />} />
         

          <Route path="report/add_test_entry/:id"          element={<Addtestentry />} />
          <Route path="patientlog/:id"          element={<PatientLog />} />
          <Route path="patientfilter/:id"       element={<PatientFilter />} />

          <Route path="lab/posreciept/:billId"  element={<PosReceipt />} />
          <Route path="lab/BillPdf/:billId"     element={<BillPdf />} />

          <Route path="billing"                element={<Billing />} />
          <Route path="/lab/billing/billpdf/:billId" element={<BillingBillPdf />} />
          <Route path="/lab/billing/posreceipt/:billId" element={<BillingPosReceipt />} />

          <Route path="payments"                element={<Payments />} />
          <Route path="profile"                 element={<Profile />} />
          <Route path="change-password"         element={<ChangePassword />} />
          <Route path="settings"                element={<Settings />} />
          <Route path="doctors"                 element={<Doctor />} />
          <Route path="testcategories"          element={<TestCategories />} />
          <Route path="test-types"              element={<TestTypes />} />
          <Route path="terms-conditions"        element={<TermsConditions />} />
          <Route path="departments"             element={<Departments />} />
          <Route path="expenses"                element={<Expenses />} />
          <Route path="/income-by-reference"    element={<IncomeByReference />} />
          <Route path="/finance-report"         element={<FinanceReport />} />
          <Route path="/report/test_receipt/:id" element={<TestReceipt />} />

          <Route path="test-profile"              element={<TestProfile />} />

        </Route>

        <Route path="*" element={<Navigate to="/home" replace />} />

      </Routes>

      <ToastContainer
        autoClose={3000}
        position="top-right"
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable={false}
      />
    </>
  );
};

const App = () => {
  const dispatch = useAppDispatch();
  const [isAppLoading, setIsAppLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      firebaseAuth,
      (user) => {
        dispatch(setCurrentUser(user ?? null));
        setIsAppLoading(false);
      },
      (error) => {
        console.error('Auth error:', error);
        dispatch(setCurrentUser(null));
        setIsAppLoading(false);
      }
    );
    return () => unsubscribe();
  }, [dispatch]);

  if (isAppLoading) return <Loading />;

  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;